import { useState, useRef, useEffect, useCallback } from 'react';
import { ClaimResponse as Claim } from '@sentara-health/sentara-api-hooks-core';
import { ClaimsFilterState } from '../types/claimsFilters';

export const useInfiniteScroll = (
  allClaimsData: Claim[],
  itemsPerPageLimit = 10,
  sortKey?: string,
  filters?: ClaimsFilterState
) => {
  const [displayedClaims, setDisplayedClaims] = useState<Claim[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [hasMoreItemsToLoad, setHasMoreItemsToLoad] = useState(true);
  const intersectionObserverTargetRef = useRef<HTMLDivElement | null>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  const currentPageNumberRef = useRef(1);
  useEffect(() => {
    currentPageNumberRef.current = currentPageNumber;
  }, [currentPageNumber]);

  const loadMoreClaimsData = useCallback(() => {
    if (!hasMoreItemsToLoad) {
      return;
    }

    const start = currentPageNumberRef.current * itemsPerPageLimit;
    const end = start + itemsPerPageLimit;

    const nextBatch = allClaimsData.slice(start, end);

    if (nextBatch.length === 0) {
      setHasMoreItemsToLoad(false);
      return;
    }

    setDisplayedClaims((prev) => [...prev, ...nextBatch]);

    const nextPage = currentPageNumberRef.current + 1;
    currentPageNumberRef.current = nextPage;
    setCurrentPageNumber(nextPage);

    const remaining = allClaimsData.length - (start + nextBatch.length);
    setHasMoreItemsToLoad(remaining > 0);
  }, [
    allClaimsData,
    itemsPerPageLimit,
    hasMoreItemsToLoad
  ]);

  useEffect(() => {
    const total = Array.isArray(allClaimsData) ? allClaimsData.length : 0;

    if (total === 0) {
      setDisplayedClaims([]);
      setCurrentPageNumber(1);

      // Reset pagination
      currentPageNumberRef.current = 1;
      setHasMoreItemsToLoad(false);
      return;
    }

    setDisplayedClaims((prevDisplayed) => {
      const prevCount = prevDisplayed.length;

      let countToShow;

      if (prevCount > 0) {
        const pagesToShow = Math.ceil(prevCount / itemsPerPageLimit);
        countToShow = Math.min(pagesToShow * itemsPerPageLimit, total);
      } else {
        countToShow = Math.min(itemsPerPageLimit, total);
      }

      const newBatch = allClaimsData.slice(0, countToShow);

      const newPage = Math.floor(countToShow / itemsPerPageLimit);
      currentPageNumberRef.current = newPage;
      setCurrentPageNumber(newPage);
      setHasMoreItemsToLoad(total > countToShow);

      return newBatch;
    });
  }, [allClaimsData, itemsPerPageLimit, sortKey, filters]);

  useEffect(() => {
    const target = intersectionObserverTargetRef.current;

    if (!target || !hasMoreItemsToLoad) {
      return;
    }

    intersectionObserverRef.current?.disconnect();

    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry?.isIntersecting) {
          loadMoreClaimsData();
        }
      },
      {
        root: null,
        threshold: 0.3,
      }
    );

    intersectionObserverRef.current.observe(target);

    return () => {
      intersectionObserverRef.current?.disconnect();
    };
  }, [
    loadMoreClaimsData,
    hasMoreItemsToLoad,
    displayedClaims.length
  ]);

  return {
    displayedClaims,
    intersectionObserverTargetRef,
    hasMoreItemsToLoad,
    loadMoreClaimsData,
  };
};
